import React, { useRef } from 'react';
import { Dropdown as AntdDropdown } from 'antd';
import {
    Button,
    Dropdown,
    DropdownMenu,
    SparkIcon,
    TagFeedback,
    TaskTypeIcon,
    Text,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import TaskBreadcrumb from '@/features/tasks/components/TaskBreadcrumb';
import DeployDropdown from '@/features/tasks/components/Task/DeployDropdown';
import {
    ViewGuidedTourButton,
    useWalkthroughAutoOpen,
    useWalkthroughContextValue,
} from '@/features/walkthrough';
import { getTaskTypeIconType, isAgenticUsecase } from '@/features/tasks/utils/taskTypeIcons';
import { getTaskTypeShortLabel } from '@/features/tasks/utils/types';

const objectiveOptionLabel = (usecase) =>
    `${getTaskTypeShortLabel(usecase.task_type)}: ${usecase.usecase_name}`;

/**
 * Spark Dropdown (Figma 3173:7654) anchored to the header kebab button.
 * Items keep the antd-shaped `{ key, label, icon, onClick, disabled, danger }`
 * contract from TaskPage so Download tooltips and existing handlers stay.
 * An item may carry `children` (same shape) — the menu then renders as a
 * flyout Spark Dropdown and the parent opens its children in a side submenu
 * panel (used for the Deploy submenu, always present once the task has a
 * completed version). The flyout opens to the left because the kebab panel is
 * anchored to the right screen edge.
 *
 * The task-management tour's "Need to make changes?" mark anchors on the
 * wrapper (`data-walkthrough="task-actions"`) and opens the menu for as long
 * as that mark is up, so the options are visible next to the bubble.
 */
function TaskKebabMenu({ items }) {
    const [open, setOpen] = useWalkthroughAutoOpen('tm-actions');
    const wrapRef = useRef(null);
    useOutsideClick([wrapRef], () => setOpen(false), open);
    useEscapeKey(() => setOpen(false), open);

    if (!items.length) return null;

    const toSparkItem = (item, isChild = false) => ({
        label: item.label,
        // `type: 'section'` rows render as non-interactive group headers
        // (e.g. "Versions" / "Query File" in the Deploy submenu).
        type: item.type,
        prefix: item.icon,
        // Trailing node (eye icon on query-file rows, Undeploy tag on the
        // deployed version row). Clicks bubble to the row's onClick.
        suffix: item.suffix,
        state: item.disabled ? 'disabled' : 'default',
        disableWhenReadOnly: item.disableWhenReadOnly,
        tone: item.danger ? 'danger' : 'default',
        ...(!isChild && item.children?.length
            ? {
                childrenTitle: item.childrenTitle,
                children: item.children.map((child) => toSparkItem(child, true)),
            }
            : {
                onClick: () => {
                    if (item.disabled) return;
                    item.onClick?.();
                    setOpen(false);
                },
            }),
    });

    const sparkItems = items.map((item) => toSparkItem(item));
    const hasSubmenus = sparkItems.some((item) => item.children?.length);

    return (
        <div ref={wrapRef} className="task-page-header__kebab" data-walkthrough="task-actions">
            <Button
                variant="tertiary"
                size="medium"
                type="button"
                aria-label="More task actions"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((prev) => !prev)}
                icon={
                    <SparkIcon
                        slug="dots"
                        size="medium"
                        className="task-page-header__dots-vertical"
                    />
                }
            />
            {open ? (
                <div className="task-page-header__kebab-panel">
                    <Dropdown
                        type={hasSubmenus ? 'flyout' : 'regular'}
                        flyoutPlacement="left"
                        items={sparkItems}
                        maxVisibleItems={sparkItems.length}
                    />
                </div>
            ) : null}
        </div>
    );
}

/**
 * Task page header per Figma (file T01Ps5U50Y85jFiC2Okmas, frame 3001:71735):
 * breadcrumb row with three compact actions on the right —
 *   1. game icon button (opens the Playground),
 *   2. vertical-dots kebab menu (Download / Retrain / Duplicate / Move Task /
 *      Settings / Delete),
 *   3. primary "Deploy ▾" button.
 *
 * Single-objective tasks get the redesigned Deploy dropdown (Figma node
 * 3480:19247) via `deployDropdown`: a Versions list with an inline Undeploy
 * tag on the deployed version, plus a Query File section (Python /
 * TypeScript / Go) opening the code modal. Undeploy lives inside that
 * dropdown, so no standalone Undeploy button is rendered.
 *
 * Multi-objective tasks keep the legacy antd menus (`deployMenuItems` +
 * secondary "Undeploy" button) built by `useTaskActions`, same contract as
 * TaskCard / TaskRow.
 */
const TaskPageHeader = ({
    title,
    usecases = [],
    activeUsecaseId,
    onObjectiveChange,
    onCreateProject,
    onAddTask,
    // Actions row
    isViewer = false,
    onOpenPlayground,
    // The SaaS platform has no Playground: TaskPage passes `false` there so
    // the game icon button is not rendered and the task-management tour
    // drops its "Test in Playground" step. Self-hosted keeps the default.
    showPlayground = true,
    kebabMenuItems = [],
    showDeploy = false,
    deployMenuItems = [],
    // Single-objective redesigned dropdown: { versions, deployedVersion,
    // loading, inProgressAction, onDeployVersion, onUndeploy, onOpenQueryFile }
    deployDropdown = null,
    // Primary action for the header button: 'deploy' (default) keeps the
    // Deploy dropdown; 'retrain' / 'reevaluate' replace it when the task's
    // datasets (resp. benchmarks or evaluation config) changed since the last
    // run — the kebab always carries Deploy as a cascading submenu item once
    // a version completed, whichever action is primary.
    // 'train' is the first-run variant of 'retrain': a task with an uploaded
    // dataset but no completed version yet (e.g. a duplicated ready-to-deploy
    // task) shows "Train" instead. Generative tasks (Summarization / Content
    // Generation) never show Deploy at all, so 'deploy' renders no primary
    // button for them while 'train' / 'retrain' / 'reevaluate' still render
    // normally.
    mainAction = 'deploy',
    onRetrainClick,
    onTrainClick,
    onReevaluateClick,
    // Tag rendered next to the title: { variant: 'success', label:
    // 'Deployed (V1)' } once deployed, or { variant: 'processing', label:
    // 'Deploy in Progress' | 'Undeploy in Progress' } while the request is
    // in flight (outlined pill with a spinning loader icon).
    deploymentTag = null,
    onDeployMenuOpenChange,
    undeployLabel = null,
    onUndeploy,
    undeployMenuItems = [],
    // Walkthrough id behind the "View Guided Tour" button (Figma 9496:14107)
    // rendered first in the actions row; `null` hides it (tasks without
    // evaluation results have nothing to tour yet).
    guidedTourId = null,
}) => {
    const isMultiObjective = usecases.length > 1;
    const activeUsecase =
        usecases.find((u) => u._id === activeUsecaseId) || usecases[0] || null;
    const iconType = getTaskTypeIconType(
        activeUsecase?.task_type,
        activeUsecase?.is_multilabel,
    );

    const undeployButton = (
        <Button
            variant="secondary"
            size="medium"
            type="button"
            disabled={isViewer}
            disableWhenReadOnly
            onClick={undeployMenuItems.length > 0 ? undefined : onUndeploy}
        >
            {undeployLabel}
        </Button>
    );

    // Primary header button, or null when the task has none (generative task
    // with nothing changed, or Deploy not available yet). Anchor of the
    // task-management tour's "Go live in one click" mark.
    const primaryAction =
        mainAction === 'train' ? (
            <Button
                className="fs-unmask"
                variant="primary"
                size="medium"
                type="button"
                disabled={isViewer}
                disableWhenReadOnly
                showStartIcon
                startIcon={<SparkIcon slug="brain-icon" size="medium" />}
                onClick={onTrainClick}
            >
                Train
            </Button>
        ) : mainAction === 'retrain' ? (
            <Button
                className="fs-unmask"
                variant="primary"
                size="medium"
                type="button"
                disabled={isViewer}
                disableWhenReadOnly
                showStartIcon
                startIcon={<SparkIcon slug="brain-icon" size="medium" />}
                onClick={onRetrainClick}
            >
                Retrain
            </Button>
        ) : mainAction === 'reevaluate' ? (
            <Button
                className="fs-unmask"
                variant="primary"
                size="medium"
                type="button"
                disabled={isViewer}
                disableWhenReadOnly
                showStartIcon
                startIcon={<SparkIcon slug="report-refresh" size="medium" />}
                onClick={onReevaluateClick}
            >
                Revaluate
            </Button>
        ) : deployDropdown ? (
            <DeployDropdown
                {...deployDropdown}
                disabled={isViewer}
                onOpenChange={onDeployMenuOpenChange}
            />
        ) : showDeploy ? (
            <AntdDropdown
                menu={{ items: deployMenuItems }}
                trigger={['click']}
                placement="bottomRight"
                disabled={isViewer || deployMenuItems.length === 0}
                onOpenChange={onDeployMenuOpenChange}
            >
                <Button
                    variant="primary"
                    size="medium"
                    type="button"
                    disabled={isViewer || deployMenuItems.length === 0}
                    disableWhenReadOnly
                    endIcon={<SparkIcon slug="chevron-down" size="medium" />}
                >
                    Deploy
                </Button>
            </AntdDropdown>
        ) : null;

    // Task-management tour context: which of its marked controls this header
    // actually renders (the Playground button is hidden on SaaS). A mark
    // whose control is missing is dropped from the tour and its count.
    useWalkthroughContextValue({
        task: {
            hasPlayground: showPlayground,
            hasActions: kebabMenuItems.length > 0,
            hasPrimaryAction: primaryAction !== null,
        },
    });

    return (
        <div className="task-page-header">
            <div className="task-page-header__top">
                <div className="task-page-header__main">
                    <TaskBreadcrumb onCreateProject={onCreateProject} onAddTask={onAddTask} />
                    <div className="task-page-header__title-row">
                        <div className="task-page-header__title-group">
                            <div className="task-page-header__type-icon">
                                <TaskTypeIcon type={iconType} agentic={isAgenticUsecase(activeUsecase)} />
                            </div>
                            <Text variant="heading-2-semibold" as="h1" className="task-page-header__title fs-unmask">
                                {title}
                            </Text>
                        </div>
                        {deploymentTag &&
                            (deploymentTag.variant === 'processing' ? (
                                // "Deploy in Progress" / "Undeploy in
                                // Progress" — outlined pill (same style as
                                // the Undeploy tag in the Deploy dropdown)
                                // with a spinning loader icon.
                                <span className="task-page-header__deployment-progress-tag fs-unmask">
                                    <SparkIcon
                                        slug="loader"
                                        size="small"
                                        className="task-page-header__deployment-progress-icon"
                                    />
                                    {deploymentTag.label}
                                </span>
                            ) : (
                                <TagFeedback
                                    {...deploymentTag}
                                    className="task-page-header__deployment-tag fs-unmask"
                                />
                            ))}
                        {isMultiObjective && activeUsecase && (
                            <div className="task-page-header__objective fs-unmask">
                                <DropdownMenu
                                    size="medium"
                                    showLabel={false}
                                    placeholder="Objective"
                                    text={objectiveOptionLabel(activeUsecase)}
                                    value={objectiveOptionLabel(activeUsecase)}
                                    items={usecases.map((uc) => ({ label: objectiveOptionLabel(uc) }))}
                                    onSelect={(item, index) => {
                                        const target = usecases[index];
                                        if (target?._id && target._id !== activeUsecaseId) {
                                            onObjectiveChange?.(target._id);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="task-page-header__actions">
                    {guidedTourId ? <ViewGuidedTourButton walkthroughId={guidedTourId} /> : null}
                    {/* icon-only Buttons must NOT receive children — the icon
                        goes through the `icon` prop so the 36x36 Figma
                        "Button Icon" sizing (no label padding) applies. */}
                    {showPlayground ? (
                        <Button
                            variant="tertiary"
                            size="medium"
                            type="button"
                            aria-label="Test in Playground"
                            data-walkthrough="task-playground"
                            onClick={onOpenPlayground}
                            icon={<SparkIcon slug="game" size="medium" />}
                        />
                    ) : null}
                    <TaskKebabMenu items={kebabMenuItems} />
                    {!deployDropdown &&
                        undeployLabel &&
                        (undeployMenuItems.length > 0 ? (
                            <AntdDropdown
                                menu={{ items: undeployMenuItems }}
                                trigger={['click']}
                                placement="bottomRight"
                                disabled={isViewer}
                            >
                                {undeployButton}
                            </AntdDropdown>
                        ) : (
                            undeployButton
                        ))}
                    {primaryAction ? (
                        <div
                            className="task-page-header__primary-action"
                            data-walkthrough="task-primary-action"
                        >
                            {primaryAction}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default TaskPageHeader;
export { TaskKebabMenu };
